<template>
  <BaseField
    :name="innerId"
    :label="label"
    :class="divClass"
    :optional="optional"
  >
    <div ref="inputGroup" class="input-group">
      <input
        readonly
        :disabled="disabled"
        :class="{ 'is-invalid': showError }"
        :value="model?.toFormat('hh:mm a') || ''"
        class="form-control"
        autocomplete="off"
        @click="open"
      />
      <button
        :disabled="disabled"
        type="button"
        class="btn btn-primary"
        @click="open"
      >
        <i class="bi-clock"></i>
      </button>
    </div>
    <HelpText
      :help-text="helpText"
      :error-message="errorMessage"
      :touched="touched"
    >
      <slot name="helpText" />
    </HelpText>
    <Popover ref="op">
      <Calendar
        v-model="jsDate"
        :show-icon="false"
        :min-date="innerMinDate"
        :max-date="innerMaxDate"
        :time-only="true"
        :panel-style="{ padding: 0, border: 'none' }"
        inline
        hour-format="12"
      />
    </Popover>
    <Dialog
      v-model:visible="dialogOpen"
      class="pt-3"
      modal
      :show-header="false"
    >
      <div class="d-flex flex-column">
        <Calendar
          v-model="jsDate"
          :show-icon="false"
          :min-date="innerMinDate"
          :max-date="innerMaxDate"
          :time-only="true"
          inline
          hour-format="12"
          :panel-style="{ padding: 0, border: 'none' }"
        />
        <button class="btn btn-primary mt-3" @click="dialogOpen = false">
          Close
        </button>
      </div>
    </Dialog>
  </BaseField>
</template>

<script lang="ts">
export default {
  name: "BaseTimePicker",
  inheritAttrs: false,
};
</script>

<script setup lang="ts">
import { templateRef } from "@vueuse/core";
import { DateTime } from "luxon";
import Calendar from "primevue/calendar";
import Dialog from "primevue/dialog";
import Popover from "primevue/popover";
import { computed, nextTick, Ref, ref, toRefs, watch } from "vue";

import HelpText from "@/shared/HelpText.vue";
import { computedModel } from "@/utilities";
import { useBreakpoints } from "@/utilities/breakpoints";
import { useField, Validators } from "@/utilities/validations";

type ModelValue = DateTime | null;

interface Props {
  name: string;
  id?: string;
  modelValue: ModelValue;
  minDate?: DateTime | null;
  maxDate?: DateTime | null;
  disabled?: boolean;

  // Field Props
  label?: string;
  optional?: boolean;
  divClass?: string;

  // Validation Props
  rules?: Validators<ModelValue>;
  formName?: string;
  helpText?: string;
}

const props = withDefaults(defineProps<Props>(), {
  id: "",
  minDate: null,
  maxDate: null,
  rules: () => [],
  disabled: false,
});

const emit = defineEmits<{
  (e: "update:modelValue", value: ModelValue): void;
}>();

const innerId = computed(() => props.id || props.name);

const { rules, modelValue } = toRefs(props);
const model = computedModel(modelValue, emit);

const { touched, validate, errorMessage, showError } = useField(
  props.name,
  model,
  rules,
  {
    formName: props.formName,
  },
);

const jsDate = computed<Date | undefined>({
  get: () => model.value?.toJSDate(),
  set: (val) => (model.value = val ? DateTime.fromJSDate(val) : null),
});

watch(model, () => {
  nextTick(() => validate());
});

const innerMaxDate = computed(() => props.maxDate?.toJSDate());
const innerMinDate = computed(() => props.minDate?.toJSDate());

const { smaller } = useBreakpoints();
const isMobile = smaller("md");

const dialogOpen = ref(false);
const op = templateRef<InstanceType<typeof Popover>>("op");
const inputGroup = ref(null) as unknown as Ref<HTMLDivElement>;

const open = (event: Event) => {
  if (isMobile.value) {
    dialogOpen.value = true;
    return;
  }
  op.value.show(event, inputGroup.value);
};
</script>

<style scoped lang="scss">
.p-calendar :deep(.p-datepicker-inline) {
  border: none;
}
</style>
